import { supabase } from '@/shared/lib/supabase';
import { EdgeFunctionError } from '@/domains/customers/services/adminCustomerService';

/**
 * Admin-side ticket operations (Phase 8d).
 *
 * Reads — direct Supabase via RLS (staff sees all rows).
 * Mutations — Edge Functions: ticket-create, ticket-reply, ticket-assign, ticket-close.
 */

interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

async function invokeEdgeFunction<TRes>(
  name: string,
  body: Record<string, unknown>
): Promise<TRes> {
  const { data, error } = await supabase.functions.invoke<TRes | ApiError>(name, {
    body,
    headers: { 'Idempotency-Key': crypto.randomUUID() },
  });
  if (error) {
    const ctx = (error as unknown as { context?: Response }).context;
    if (ctx && typeof ctx.clone === 'function') {
      try {
        const parsed = (await ctx.clone().json()) as ApiError;
        if (parsed.code && parsed.message) {
          throw new EdgeFunctionError(parsed.code, parsed.message, ctx.status, parsed.details);
        }
      } catch (parseErr) {
        if (parseErr instanceof EdgeFunctionError) throw parseErr;
      }
    }
    throw new Error(error.message);
  }
  if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
    const apiErr = data as ApiError;
    throw new EdgeFunctionError(apiErr.code, apiErr.message, 200, apiErr.details);
  }
  return data as TRes;
}

export type TicketStatus = 'open' | 'pending_customer' | 'closed';
export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface AdminTicketRow {
  id: string;
  number: string;
  customer_id: string;
  department_id: string;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  related_cpanel_username: string | null;
  related_domain_name: string | null;
  related_invoice_id: string | null;
  assigned_staff_id: string | null;
  last_replied_at: string | null;
  last_replied_by_kind: 'staff' | 'customer' | null;
  closed_at: string | null;
  first_response_at: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminTicketRowEnriched extends AdminTicketRow {
  customer_email: string | null;
  customer_name: string;
  department_name: string | null;
  department_sla_hours: number | null;
  // Sprint Admin-B B-2 — business-hour-aware SLA state from v_admin_ticket_sla.
  // NULL means "closed OR already first-responded" (badge should not render).
  sla_state: 'within' | 'at_risk' | 'breached' | null;
  sla_deadline: string | null;
  sla_remaining_minutes: number | null;
}

interface TicketSlaRow {
  ticket_id: string;
  sla_state: 'within' | 'at_risk' | 'breached' | null;
  sla_deadline: string | null;
  sla_remaining_minutes: number | null;
}

export interface TicketMessage {
  id: string;
  ticket_id: string;
  author_kind: 'staff' | 'customer' | 'system';
  author_id: string | null;
  body: string;
  is_internal_note: boolean;
  created_at: string;
}

export interface Department {
  id: string;
  name: string;
  sla_hours: number;
  is_active: boolean;
}

const TICKET_COLS =
  'id, number, customer_id, department_id, subject, status, priority, related_cpanel_username, related_domain_name, related_invoice_id, assigned_staff_id, last_replied_at, last_replied_by_kind, closed_at, first_response_at, resolved_at, created_at, updated_at';

export interface ListFilters {
  status?: TicketStatus | 'all';
  department_id?: string;
  priority?: TicketPriority;
  assigned_to_me?: string; // staff_id
  search?: string;
}

export const adminTicketService = {
  async list(filters: ListFilters = {}): Promise<AdminTicketRowEnriched[]> {
    let query = supabase
      .from('ticket')
      .select(TICKET_COLS)
      .order('last_replied_at', { ascending: false, nullsFirst: false });

    if (filters.status && filters.status !== 'all') query = query.eq('status', filters.status);
    if (filters.department_id) query = query.eq('department_id', filters.department_id);
    if (filters.priority) query = query.eq('priority', filters.priority);
    if (filters.assigned_to_me) query = query.eq('assigned_staff_id', filters.assigned_to_me);
    if (filters.search) query = query.ilike('subject', `%${filters.search}%`);

    const { data: rows, error } = await query;
    if (error) throw new Error(`Could not load tickets: ${error.message}`);

    const tickets = (rows ?? []) as AdminTicketRow[];
    if (tickets.length === 0) return [];

    // Enrich with customer + department + SLA lookups in batch.
    // Sprint Admin-B B-2 — SLA fields come from v_admin_ticket_sla (business-hour
    // aware via add_working_hours()) instead of the legacy client-side slaState().
    const customerIds = [...new Set(tickets.map((t) => t.customer_id))];
    const departmentIds = [...new Set(tickets.map((t) => t.department_id))];
    const ticketIds = tickets.map((t) => t.id);

    const [{ data: profiles }, { data: depts }, { data: slaRows }] = await Promise.all([
      // Contact-only view (name/email) — lets scoped support agents resolve the
      // ticket raiser without RLS access to customer_profile PII (2026-07-06).
      supabase.from('v_support_customer_contact').select('id, full_name, billing_email').in('id', customerIds),
      supabase.from('department').select('id, name, sla_hours').in('id', departmentIds),
      supabase
        .from('v_admin_ticket_sla')
        .select('ticket_id, sla_state, sla_deadline, sla_remaining_minutes')
        .in('ticket_id', ticketIds),
    ]);

    const profileMap = new Map(
      (profiles ?? []).map((p) => [
        (p as { id: string }).id,
        p as { id: string; full_name: string | null; billing_email: string | null },
      ])
    );
    const deptMap = new Map(
      (depts ?? []).map((d) => [
        (d as { id: string }).id,
        d as { id: string; name: string; sla_hours: number },
      ])
    );
    const slaMap = new Map(
      (slaRows ?? []).map((s) => [(s as TicketSlaRow).ticket_id, s as TicketSlaRow]),
    );

    return tickets.map((t) => {
      const profile = profileMap.get(t.customer_id);
      const dept = deptMap.get(t.department_id);
      const sla = slaMap.get(t.id);
      return {
        ...t,
        customer_email: profile?.billing_email ?? null,
        customer_name: profile?.full_name ?? '—',
        department_name: dept?.name ?? null,
        department_sla_hours: dept?.sla_hours ?? null,
        sla_state: sla?.sla_state ?? null,
        sla_deadline: sla?.sla_deadline ?? null,
        sla_remaining_minutes: sla?.sla_remaining_minutes ?? null,
      };
    });
  },

  async getOne(ticketId: string): Promise<AdminTicketRowEnriched | null> {
    const { data: row, error } = await supabase
      .from('ticket')
      .select(TICKET_COLS)
      .eq('id', ticketId)
      .maybeSingle();
    if (error) throw new Error(`Could not load ticket: ${error.message}`);
    if (!row) return null;
    const t = row as AdminTicketRow;

    const [{ data: profile }, { data: dept }, { data: sla }] = await Promise.all([
      supabase
        .from('v_support_customer_contact')
        .select('id, full_name, billing_email')
        .eq('id', t.customer_id)
        .maybeSingle(),
      supabase
        .from('department')
        .select('id, name, sla_hours')
        .eq('id', t.department_id)
        .maybeSingle(),
      // Sprint Admin-B B-2 — SLA state from the view.
      supabase
        .from('v_admin_ticket_sla')
        .select('sla_state, sla_deadline, sla_remaining_minutes')
        .eq('ticket_id', ticketId)
        .maybeSingle(),
    ]);

    return {
      ...t,
      customer_email:
        (profile as { billing_email: string | null } | null)?.billing_email ?? null,
      customer_name: (profile as { full_name: string | null } | null)?.full_name ?? '—',
      department_name: (dept as { name: string } | null)?.name ?? null,
      department_sla_hours: (dept as { sla_hours: number } | null)?.sla_hours ?? null,
      sla_state: (sla as TicketSlaRow | null)?.sla_state ?? null,
      sla_deadline: (sla as TicketSlaRow | null)?.sla_deadline ?? null,
      sla_remaining_minutes: (sla as TicketSlaRow | null)?.sla_remaining_minutes ?? null,
    };
  },

  async listMessages(ticketId: string): Promise<TicketMessage[]> {
    const { data, error } = await supabase
      .from('ticket_message')
      .select('id, ticket_id, author_kind, author_id, body, is_internal_note, created_at')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true });
    if (error) throw new Error(`Could not load messages: ${error.message}`);
    return (data ?? []) as TicketMessage[];
  },

  async listDepartments(): Promise<Department[]> {
    const { data, error } = await supabase
      .from('department')
      .select('id, name, sla_hours, is_active')
      .eq('is_active', true)
      .order('name', { ascending: true });
    if (error) throw new Error(`Could not load departments: ${error.message}`);
    return (data ?? []) as Department[];
  },

  async listStaff(): Promise<{ id: string; email: string; name: string }[]> {
    // QA-2: SECURITY DEFINER RPC that filters auth.users by role and gates on
    // is_staff(auth.uid()). Anon/authenticated have no direct SELECT on
    // auth.users, so we cannot query that table from the client.
    const { data, error } = await supabase.rpc('list_staff_users');
    if (error) throw new Error(`Could not load staff: ${error.message}`);
    const rows = (data ?? []) as Array<{ id: string; email: string; full_name: string }>;
    return rows.map((r) => ({ id: r.id, email: r.email, name: r.full_name || r.email }));
  },

  async create(input: {
    customer_id: string;
    department_id: string;
    subject: string;
    body: string;
    priority?: TicketPriority;
    related_cpanel_username?: string;
    related_domain_name?: string;
    related_invoice_id?: string;
  }): Promise<{ ok: true; ticket_id: string; ticket_number: string }> {
    return invokeEdgeFunction('ticket-create', input as unknown as Record<string, unknown>);
  },

  async reply(input: {
    ticket_id: string;
    body: string;
    is_internal_note?: boolean;
  }): Promise<{ ok: true; message_id: string }> {
    return invokeEdgeFunction('ticket-reply', input as unknown as Record<string, unknown>);
  },

  async assign(input: {
    ticket_id: string;
    assigned_staff_id: string | null;
  }): Promise<{ ok: true }> {
    return invokeEdgeFunction('ticket-assign', input as unknown as Record<string, unknown>);
  },

  async close(input: { ticket_id: string; resolution_note?: string }): Promise<{ ok: true }> {
    return invokeEdgeFunction('ticket-close', input as unknown as Record<string, unknown>);
  },
};

export const PRIORITY_OPTIONS: { value: TicketPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export const STATUS_LABELS: Record<TicketStatus, string> = {
  open: 'Open',
  pending_customer: 'Awaiting customer',
  closed: 'Closed',
};
